// ════════════════════════════════════════════════════════════════════════════
//  The window stack, remembered (OD-6)
//  ---------------------------------------------------------------------------
//  Which windows are open is a fact about THIS DEVICE — like the open board
//  (md-board.ts's `md:boardOpen`), it is persisted so a refresh is not an
//  eviction, and it is NEVER SYNCED: this module offers no sync door of any
//  kind and md-sync.js's whitelist has never heard its name. What you had open
//  on your phone is not a claim about your desktop.
//
//  One blob under `md:ui`, through the storage chokepoint, holding exactly the
//  five facts of the stack: { projectsOpen, projectsView, projectWindowId,
//  databasesOpen, craftsOpen }. Anything else handed to save is dropped at
//  the door — in particular the SHEET is not remembered (D7): a record lives
//  from its first keystroke so a reload loses nothing, and a sheet that
//  reopens uninvited is a surprise, not a memory.
//
//  LOAD VALIDATES EVERYTHING IT RESTORES, with the store's standing quarantine
//  bias: restore only what still resolves, and when in doubt restore less.
//    • an unreadable or mis-shaped blob is discarded whole — the default day;
//    • a projectWindowId that no longer resolves (deleted on another device)
//      is dropped silently and Projects opens alone;
//    • a projectsView naming a deleted pursuit falls back to the root;
//    • a project window never restores over a closed Workshop, because it
//      STANDS on the Workshop (md-app.jsx) and cannot stand on nothing.
//
//  A stack with every window closed clears the key rather than writing a blob
//  of falses — the bargain md-record-status.ts makes when the last word leaves
//  its map: a device with nothing open keeps no record of it.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  const KEY = 'md:ui';

  interface MdUiState {
    projectsOpen: boolean;
    projectsView: { kind: 'domain' | 'pursuit'; id: string } | null;
    projectWindowId: string | null;
    databasesOpen: boolean;
    craftsOpen: boolean;
  }

  const closed = (): MdUiState => ({
    projectsOpen: false, projectsView: null, projectWindowId: null,
    databasesOpen: false, craftsOpen: false });

  /* ── SIZES, WHICH ARE A DIFFERENT KIND OF FACT (CS-4) ────────────────────
     Every resizable surface re-derived its size on every mount: the Workshop
     opened at `board`, the Project window at `full`, whatever you last chose
     thrown away the moment you closed it. One map, keyed surface × device
     SHAPE, riding this same device-local blob.

     Per shape, because a size chosen on a laptop may not exist on the iPad
     beside it, and because the same iPad rotated is a different screen. The
     device still decides the default; your last explicit choice is what sticks.

     THE ONE COLLISION WITH THE STACK, AND HOW IT IS SETTLED. This module
     removes the key when every window is closed — a device with nothing open
     keeps no record of it. But a size is not a record of what is open, it is a
     preference, and closing a window must not forget your preferences. So the
     clear drops the STACK and keeps the sizes, and the key goes only when there
     is neither. The bargain is intact: the blob still says nothing about what
     you had open.

     Both lists are closed on purpose. An unbounded map keyed by whatever a
     caller happened to pass is a place for junk to accumulate on a device
     nobody can inspect, and this is the same door discipline the five stack
     facts already pass through. */
  /* THE THREE WINDOWS, AND NOT THE SHEET (Marwan, 2026-08-21). CS-4 built this
     for four surfaces. A window is a place you go back to and arrange, so how
     you left it is worth keeping; a sheet is a thing you open to write one line,
     and remembering that you once spread one out meant every record afterwards
     opened full screen. The sheet holds its rung for as long as it is open and
     forgets it on the way out (md-compose.jsx's useMdSheetStand).

     Dropping the name here is what makes that true rather than merely intended:
     the door is closed, so a stale `sheet` entry from an earlier build is not
     read back, and no caller can put one there again. */
  const SURFACES = ['projects', 'databases', 'project'];
  const SHAPES = ['desktop', 'tablet-wide', 'tablet-tall', 'phone', 'phone-wide'];

  /* THE THREE WINDOWS' TWO SIZES, and the one they get where there is no board
     to float over. They live HERE rather than beside the window geometry in
     md-databases.jsx, because a window that reads a constant off a SIBLING
     window is a window that cannot be rendered without it — which is precisely
     how the Workshop's own tests broke when this pair was published from there.
     Every surface already depends on this module for its size; nothing new is
     owed by putting the vocabulary of sizes in the same place.

     Frozen arrays, shared: useMdRemembered keys its callback on the list, and a
     fresh array per render would rebuild that callback every render. */
  const MD_SPACE_SIZES = Object.freeze(['board', 'full']) as unknown as string[];
  const MD_SPACE_FULL_ONLY = Object.freeze(['full']) as unknown as string[];

  type SizeMap = Record<string, Record<string, string>>;

  /* The store may not be under us. Three surfaces now ask this module for their
     size on every render, and a component that throws because a module below it
     was not loaded is a component that cannot be rendered in isolation. No
     store means nothing remembered, which is exactly the right answer. */
  const canRead = (): boolean => typeof g.mdRead === 'function';
  const readBlob = (): unknown => (canRead() ? g.mdRead(KEY, null) : null);

  /** The sizes map as stored, or an empty one. Never throws, never guesses. */
  function storedSizes(): SizeMap {
    const raw = readBlob();
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const sizes = (raw as Record<string, unknown>).sizes;
    if (!sizes || typeof sizes !== 'object' || Array.isArray(sizes)) return {};
    const out: SizeMap = {};
    for (const surface of SURFACES) {
      const per = (sizes as Record<string, unknown>)[surface];
      if (!per || typeof per !== 'object' || Array.isArray(per)) continue;
      const kept: Record<string, string> = {};
      for (const shape of SHAPES) {
        const v = (per as Record<string, unknown>)[shape];
        if (typeof v === 'string' && v) kept[shape] = v;
      }
      if (Object.keys(kept).length) out[surface] = kept;
    }
    return out;
  }

  /** What this surface was last set to on this shape, or null. */
  function mdUiSizeGet(surface: unknown, shape: unknown): string | null {
    const per = storedSizes()[String(surface)];
    const v = per ? per[String(shape)] : undefined;
    return typeof v === 'string' && v ? v : null;
  }

  /** Remember a size. False if the door refused it, or the device did. */
  function mdUiSizeSet(surface: unknown, shape: unknown, size: unknown): boolean {
    if (SURFACES.indexOf(String(surface)) < 0) return false;
    if (SHAPES.indexOf(String(shape)) < 0) return false;
    if (typeof size !== 'string' || !size) return false;
    if (typeof g.mdWrite !== 'function') return false;
    const sizes = storedSizes();
    const per = sizes[String(surface)] || (sizes[String(surface)] = {});
    per[String(shape)] = size;
    return writeBlob(stackOf(readBlob()), sizes);
  }

  /* One writer for both halves, so neither can drop the other by forgetting to
     read it first. An empty stack AND an empty map means the key itself goes. */
  function writeBlob(stack: Record<string, unknown>, sizes: SizeMap): boolean {
    const doc: Record<string, unknown> = { ...stack };
    if (Object.keys(sizes).length) doc.sizes = sizes;
    if (!Object.keys(doc).length) {
      if (readBlob() == null) return true;
      return g.mdRemove(KEY);
    }
    return g.mdWrite(KEY, doc);
  }

  /** The stack half of whatever is stored, as written — not as validated. */
  function stackOf(raw: unknown): Record<string, unknown> {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const s = raw as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of ['projectsOpen', 'databasesOpen', 'craftsOpen',
      'projectsView', 'projectWindowId']) {
      if (k in s) out[k] = s[k];
    }
    return out;
  }

  /** A remembered size, made safe for the surface it is being restored into.
   *  `allowed` is the caller's own list of what it can draw right now, and
   *  `fallback` its own default — the Project window opens `full` where the
   *  other two open `board`, and that difference is theirs to keep. */
  function mdUiRemembered(surface: unknown, layout: unknown,
    allowed: unknown, fallback: string): string {
    const shape = g.mdDeviceShape ? g.mdDeviceShape(layout) : 'desktop';
    const list = Array.isArray(allowed) ? allowed : [];
    const v = mdUiSizeGet(surface, shape);
    return v != null && list.indexOf(v) >= 0 ? v : fallback;
  }

  /* The React face of the two above: read once at mount, re-read when the SHAPE
     changes underneath (a rotation, a dragged window), and write on every
     explicit choice. The setter IS the write — there is no second call for a
     caller to forget to make, which is how four surfaces share one behaviour
     without four chances to get it wrong. */
  function useMdRemembered(surface: string, layout: any,
    allowed: string[], fallback: string): [string, (next: string) => void] {
    const R = g.React;
    const shape = g.mdDeviceShape ? g.mdDeviceShape(layout) : 'desktop';
    const [size, setSize] = R.useState(() => mdUiRemembered(surface, layout, allowed, fallback));
    /* `allowed` and `fallback` are deliberately NOT dependencies: they are
       rebuilt every render, and a surface whose ladder changed without its
       shape changing does not exist. The shape is the honest trigger.

       THE MOUNT RUN IS SKIPPED, and not for tidiness. useState already asked
       the same question a moment ago, so re-answering it writes the state a
       component has — and a `useState` set at mount, even to a value React
       discards, costs that component its EAGER BAILOUT: from then on React
       cannot decide that a same-value update changes nothing without first
       rendering to find out. The Workshop's drag depends on exactly that
       bailout to sit still while the pointer travels across a row, and this
       effect quietly cost it ten redraws a row until the mount run went away. */
    const mounted = R.useRef(false);
    R.useEffect(() => {
      if (!mounted.current) { mounted.current = true; return; }
      setSize(mdUiRemembered(surface, layout, allowed, fallback));
    }, [surface, shape]);
    const choose = R.useCallback((next: string) => {
      if (allowed.indexOf(next) < 0) return;
      setSize(next);
      mdUiSizeSet(surface, shape, next);
    }, [surface, shape, allowed.join('|')]);
    // Guarded on the way out too, so a stale state can never draw a rectangle
    // this surface does not have.
    return [allowed.indexOf(size) >= 0 ? size : fallback, choose];
  }

  /** Remember the stack. Returns false only when the write itself failed. */
  function mdUiSave(state: unknown): boolean {
    const s = (state && typeof state === 'object' ? state : {}) as Record<string, unknown>;
    const projectsOpen = s.projectsOpen === true;
    const databasesOpen = s.databasesOpen === true;
    const craftsOpen = s.craftsOpen === true;
    if (!projectsOpen && !databasesOpen && !craftsOpen) {
      return writeBlob({}, storedSizes());
    }
    const view = s.projectsView as Record<string, unknown> | null;
    const doc: Record<string, unknown> = { projectsOpen, databasesOpen, craftsOpen };
    /* The view and the window ride only on an open Workshop — they are ITS
       facts, and writing them beside a closed one would store a contradiction
       for load to have to adjudicate later. */
    if (projectsOpen && view && typeof view === 'object'
      && (view.kind === 'domain' || view.kind === 'pursuit')
      && typeof view.id === 'string' && view.id) {
      doc.projectsView = { kind: view.kind, id: view.id };
    }
    if (projectsOpen && typeof s.projectWindowId === 'string' && s.projectWindowId) {
      doc.projectWindowId = s.projectWindowId;
    }
    return writeBlob(doc, storedSizes());
  }

  /** The stack as it was — every fact re-proved against what still exists. */
  function mdUiLoad(): MdUiState {
    const out = closed();
    const raw = g.mdRead(KEY, null);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
    const s = raw as Record<string, unknown>;
    out.projectsOpen = s.projectsOpen === true;
    out.databasesOpen = s.databasesOpen === true;
    out.craftsOpen = s.craftsOpen === true;
    const view = s.projectsView as Record<string, unknown> | null;
    if (out.projectsOpen && view && typeof view === 'object'
      && typeof view.id === 'string' && view.id) {
      if (view.kind === 'domain' && g.mdDomainById && g.mdDomainById(view.id)) {
        out.projectsView = { kind: 'domain', id: view.id };
      } else if (view.kind === 'pursuit' && g.mdPursuitById && g.mdPursuitById(view.id)) {
        out.projectsView = { kind: 'pursuit', id: view.id };
      }
    }
    if (out.projectsOpen && typeof s.projectWindowId === 'string' && s.projectWindowId
      && g.mdProjectById && g.mdProjectById(s.projectWindowId)) {
      out.projectWindowId = s.projectWindowId;
    }
    return out;
  }

  Object.assign(g, { mdUiSave, mdUiLoad, mdUiSizeGet, mdUiSizeSet,
    mdUiRemembered, useMdRemembered, MD_SPACE_SIZES, MD_SPACE_FULL_ONLY });
})(window as any);
